import {
	Application,
	Extension,
	GeofenceLocation,
	OptionRelatedInstituteSettings,
} from "@verity/proctoring-option/types";
import { ThirdPartyPlatform, VerityInstituteSettings } from "../../../types/common";
import { useCallback, useState } from "react";
import { AssessmentUuids } from "../../../api/fetch";
import { EnabledProctoringOptions } from "@verity/api-client/types";
import { intIdToUuid } from "../../../api/uuid";
import { useProctoringHelpers } from "../../proctoring-options/hooks/useProctoringHelpers";
import { UseEditQuizStateProps } from "./useEditQuizState";
import i18nPackage from "../../../i18n/i18n";

type UseSettingsStateProps = Pick<
	UseEditQuizStateProps,
	"quizInfo" | "logger" | "api" | "proctoringOption" | "courseName" | "assessmentUuids"
>;

type AddAssessmentIdProps = Omit<AssessmentUuids, "assessmentUuid"> & {
	assessmentUuid?: string;
};

/**
 * Contains state related to settings and fetching...?
 */
export default function useSettingsState({
	api,
	quizInfo,
	logger,
	proctoringOption,
	courseName,
	assessmentUuids,
}: UseSettingsStateProps) {
	const {
		getAllApplications,
		getAllExtensions,
		getThirdPartyPlatforms,
		getInstitute,
		getUserInstituteId,
		getAssessmentByAssessmentInfo,
		getCourseById,
		getCourseByLmsCourseId,
		getInstituteSettings,
		getCourseSettings,
		getVerityInstituteSettings,
		getEnabledProctoringOptions,
	} = api;

	const [optionRelatedInstituteSettings, setOptionRelatedInstituteSettings] =
		useState<OptionRelatedInstituteSettings>();
	const [enabledProctoringOptions, setEnabledProctoringOptions] = useState<EnabledProctoringOptions>({
		proctoringMethod: ["automated", "live", "pop-in", "pop-in-post-review"],
	});
	const [verityInstituteSettings, setVerityInstituteSettings] = useState<VerityInstituteSettings>();
	const [courseSettings, setCourseSettings] = useState<Awaited<ReturnType<typeof getCourseSettings>>>();

	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);
	const [thirdPartyPlatforms, setThirdPartyPlatforms] = useState<ThirdPartyPlatform[]>([]);
	const [loading, setLoading] = useState(true);

	// For fetches that don't require parameters
	const fetchStaticData = useCallback(async () => {
		const [appRes, extRes, thirdPartyRes, veritySettingsRes, enableRes] = await Promise.all([
			getAllApplications(),
			getAllExtensions(),
			getThirdPartyPlatforms(),
			getVerityInstituteSettings(),
			getEnabledProctoringOptions({ assessmentInfo: quizInfo, courseName }),
		]);

		setApplications(appRes);
		setExtensions(extRes);
		setVerityInstituteSettings(veritySettingsRes);
		setEnabledProctoringOptions(enableRes);

		const platforms = Object.values(thirdPartyRes.selectedPlatformRoster);
		platforms.push({
			platformName: i18nPackage.t("editQuizPage.confirmationBlockTSX.defaultPlatform"),
			platformId: -1,
		});
		setThirdPartyPlatforms(platforms);

		return {
			applications: appRes,
			extensions: extRes,
			verityInstituteSettings: veritySettingsRes,
			enabledProctoringOption: enableRes,
			thirdPartyPlatforms: platforms,
		};
	}, [
		courseName,
		getAllApplications,
		getAllExtensions,
		getEnabledProctoringOptions,
		getThirdPartyPlatforms,
		getVerityInstituteSettings,
		quizInfo,
	]);

	// For fetches that require an institute id
	const fetchInstituteData = useCallback(
		async (instituteUuid: string) => {
			const [instituteSettingRes] = await Promise.all([getInstituteSettings(instituteUuid)]);
			setOptionRelatedInstituteSettings(instituteSettingRes);
		},
		[getInstituteSettings],
	);

	const fetchCourseData = useCallback(
		async (courseId: number) => {
			const settings = await getCourseSettings(courseId);
			setCourseSettings(settings);
		},
		[getCourseSettings],
	);

	const getAssessmentUuids = useCallback(async () => {
		const quizId = quizInfo.quizId;
		let instituteId: number | undefined;
		try {
			instituteId = await getUserInstituteId();
		} catch (e) {
			void logger?.error(e);
		}
		let assessment: Awaited<ReturnType<typeof getAssessmentByAssessmentInfo>> | undefined = undefined;

		if (quizId) {
			assessment = await getAssessmentByAssessmentInfo({
				...quizInfo,
				quizId,
			});
		}
		let course: { accessControlUuid: string; instituteId: number; id: number };
		if (assessment) {
			course = await getCourseById(assessment.courseId);
		} else if (quizInfo.courseId) {
			course = await getCourseByLmsCourseId({
				lmsCourseId: quizInfo.courseId,
				instituteId,
			});
		} else {
			throw new Error("Course could not be found");
		}

		const institute = await getInstitute(course.instituteId);

		const response = {
			assessmentUuid: assessment ? intIdToUuid(assessment.id) : undefined,
			courseUuid: course.accessControlUuid,
			instituteUuid: institute.uuid,
		};
		assessmentUuids.current = response;
		return {
			assessmentUUids: response,
			verityCourseId: course.id,
		};
	}, [
		getAssessmentByAssessmentInfo,
		getCourseById,
		getCourseByLmsCourseId,
		getInstitute,
		getUserInstituteId,
		logger,
		quizInfo,
		assessmentUuids,
	]);

	const addAssessmentId = useCallback(
		async ({ instituteUuid, courseUuid, assessmentUuid }: AddAssessmentIdProps): Promise<AssessmentUuids | null> => {
			let newAssessmentUuid: string;
			const quizId = quizInfo.quizId;
			if (!quizId) {
				return null;
			}
			if (assessmentUuid) {
				newAssessmentUuid = assessmentUuid;
			} else {
				const assessment = await getAssessmentByAssessmentInfo({
					...quizInfo,
					quizId: quizId,
				});
				if (!assessment) {
					throw new Error("Assessment not found");
				}
				newAssessmentUuid = intIdToUuid(assessment.id);
				assessmentUuids.current = { instituteUuid, courseUuid, assessmentUuid: newAssessmentUuid };
			}
			return { instituteUuid, courseUuid, assessmentUuid: newAssessmentUuid };
		},
		[assessmentUuids, getAssessmentByAssessmentInfo, quizInfo],
	);

	const { allowedCategories } = useProctoringHelpers({
		enabledProctoringOptions,
		optionRelatedInstituteSettings,
		proctoringOption,
		verityInstituteSettings,
	});

	const isProctoringOptionAllowed = useCallback(
		<Option extends keyof EnabledProctoringOptions>(option: Option, value: EnabledProctoringOptions[Option][number]) =>
			enabledProctoringOptions[option].includes(value),
		[enabledProctoringOptions],
	);

	return {
		action: {
			fetchStaticData,
			fetchInstituteData,
			fetchCourseData,
			getAssessmentUuids,
			setLoading,
			addAssessmentId,
			isProctoringOptionAllowed,
		},
		state: {
			proctoringData: {
				applications,
				extensions,
				thirdPartyPlatforms,
				instituteKeywords: optionRelatedInstituteSettings?.flaggedKeywords ?? [],
				geofences: (courseSettings?.geofenceLocations as GeofenceLocation[]) ?? [],
			},
			assessmentUuids,
			verityInstituteSettings,
			enabledProctoringOptions,
			optionRelatedInstituteSettings,
			loading,
			allowedCategories,
		},
	};
}

export type ProctoringDataType = ReturnType<typeof useSettingsState>["state"]["proctoringData"];
